import * as _ from 'lodash';
import type { FunctionComponent } from 'react';

import useProjectReleaseGroups from '../../../../../hooks/api/projects/useProjectReleaseGroups';
import useToggle from '../../../../../hooks/useToggle';
import FromNow from '../../../../generic/FromNow/FromNow';
import Icon from '../../../../generic/Icon/Icon';
import { icons } from '../../../../generic/Icon/icons';
import RouterLink from '../../../../generic/Link/RouterLink';
import Loader from '../../../../generic/Loader';
import type { ReleaseGroupsForProject } from '../../../../../../shared/types/routes/projects';

import { InfoCard } from './InfoCard';

import styles from './ReleaseGroupsCard.module.less';

const MAX_RELEASE_TAGS = 5;
const MAX_RELEASE_GROUPS = 10;

type ReleaseTagsProps = {
  tags: string[];
};

const ReleaseTags: FunctionComponent<ReleaseTagsProps> = ({ tags }) => {
  const [shouldShowMore, toggleShowMore] = useToggle();
  const extraTagCount = tags.length > MAX_RELEASE_TAGS ? tags.length - MAX_RELEASE_TAGS : 0;
  const visibleTags = shouldShowMore ? tags : _.take(tags, MAX_RELEASE_TAGS);
  const expandText = shouldShowMore ? 'hide' : `+${extraTagCount} more`;

  return (
    <div className={styles.tags}>
      <span>{visibleTags.join(', ')}</span>
      {extraTagCount > 0 && (
        <button type="button" className={styles.expand} onClick={toggleShowMore}>
          {expandText}
        </button>
      )}
    </div>
  );
};

type ReleaseGroupRowProps = {
  releaseGroup: ReleaseGroupsForProject;
};

const ReleaseGroupRow: FunctionComponent<ReleaseGroupRowProps> = ({ releaseGroup }) => {
  const tags = releaseGroup.revisions.flatMap((r) => r.releases.map((release) => release.releaseTitle));

  return (
    <div className={styles.group}>
      <div className={styles.groupHeader}>
        <RouterLink to={`/projects/group/${releaseGroup.releaseGroupId}`} className={styles.groupTitle}>
          {releaseGroup.releaseGroupTitle}
        </RouterLink>
        <span className={styles.chevron}>
          <Icon icon={icons.chevronRight} />
        </span>
      </div>
      {tags.length > 0 && <ReleaseTags tags={tags} />}
      <div className={styles.date}>Created <FromNow date={releaseGroup.createdAt} /></div>
    </div>
  );
};

type Props = {
  projectLocator?: string;
  isLoading: boolean;
};

/**
 * Card on the project summary page that shows the release groups the project belongs to.
 */
export const ReleaseGroupsCard: FunctionComponent<Props> = ({ projectLocator, isLoading }) => {
  const { data, isLoading: isFetchingGroups, error } = useProjectReleaseGroups(
    projectLocator ?? '',
    !!projectLocator,
  );
  const releaseGroups = data?.releaseGroups ?? [];
  const visible = _.take(releaseGroups, MAX_RELEASE_GROUPS);
  const extraCount = releaseGroups.length > MAX_RELEASE_GROUPS
    ? releaseGroups.length - MAX_RELEASE_GROUPS
    : 0;
  const isPending = isLoading || isFetchingGroups;

  return (
    <InfoCard
      title="Release Groups"
      icon={icons.folderOpen}
      count={isPending ? undefined : releaseGroups.length}
    >
      {isPending && <Loader />}
      {!isPending && error && <span className={styles.error}>Error loading release groups</span>}
      {!isPending && !error && releaseGroups.length === 0 && (
        <span className={styles.empty}>This project is not a part of any release groups</span>
      )}
      {!isPending && !error && releaseGroups.length > 0 && (
        <div className={styles.groups}>
          {visible.map((releaseGroup) => (
            <ReleaseGroupRow key={releaseGroup.releaseGroupId} releaseGroup={releaseGroup} />
          ))}
          {extraCount > 0 && (
            <RouterLink to="/release-groups">+{extraCount} more</RouterLink>
          )}
        </div>
      )}
    </InfoCard>
  );
};
